import { t } from "@lingui/macro";
import {
  PlannedExpensesFilterOptionsDocument,
  type PlannedExpensesFilterOptionsQuery,
  type PlannedExpensesFilterOptionsQueryVariables,
  PlannedExpensesOrderByColumnEnum,
  PlannedExpensesOverviewDocument,
  type PlannedExpensesOverviewQuery,
  type PlannedExpensesOverviewQueryVariables,
  PlannedExpensesOverviewSummaryDocument,
  type PlannedExpensesOverviewSummaryQuery,
  type PlannedExpensesOverviewSummaryQueryVariables,
  type PlannedExpensesSummaryFragment,
  PlannedExpensesWhereColumn,
  ProjectStatusEnum,
  ProjectTypeEnum,
  SortOrder,
  SqlOperator,
} from "@src/__generated__/graphql";
import type { PlannedExpensesTableRow } from "@src/components/modules/projects/planned-expenses/models";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import {
  getBooleanFilterOptions,
  getBudgetItemTypeFilterOptions,
} from "@src/utils/components/filters/constants";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import mapToOptions from "@src/utils/map-to-options";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { getQueryParamOptional } from "@src/utils/routing";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";
import type { Query } from "nextjs-routes";

export class StorePlannedExpenses {
  @observable.ref
  tableData: PlannedExpensesTableRow[] = [];

  @observable.ref
  summary?: PlannedExpensesSummaryFragment;

  @observable.ref
  searchTerm = "";

  private overviewFetcher = new FetchHelper<
    PlannedExpensesOverviewQuery,
    PlannedExpensesOverviewQueryVariables
  >(PlannedExpensesOverviewDocument);

  private summaryFetcher = new FetchHelper<
    PlannedExpensesOverviewSummaryQuery,
    PlannedExpensesOverviewSummaryQueryVariables
  >(PlannedExpensesOverviewSummaryDocument);

  private filterOptionsFetcher = new FetchHelper<
    PlannedExpensesFilterOptionsQuery,
    PlannedExpensesFilterOptionsQueryVariables
  >(PlannedExpensesFilterOptionsDocument);

  pagination = new PaginationState("projectsPlannedExpenses", {
    onChangePagination: () => {
      this.tableData = [];
      this.fetchTableData();
    },
  });

  private orderBy = new OrderBy<PlannedExpensesOrderByColumnEnum>([
    {
      column: PlannedExpensesOrderByColumnEnum.ProjectCode,
      order: SortOrder.Asc,
    },
  ]);

  where = new Filters<PlannedExpensesWhereColumn>(
    [
      new Filter({
        column: PlannedExpensesWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Client/Brand`,
        tagTitle: t`Brand`,
        options: [],
        sectioned: true,
        hasSelectAllOption: true,
      }),
      new Filter({
        column: PlannedExpensesWhereColumn.ProjectId,
        operator: SqlOperator.In,
        title: t`Project`,
        options: [],
      }),
      new Filter({
        column: PlannedExpensesWhereColumn.ProjectManagerId,
        operator: SqlOperator.In,
        title: t`Project Manager`,
        options: [],
        hasSelectAllOption: true,
      }),
      new Filter({
        column: PlannedExpensesWhereColumn.ProjectCategoryId,
        operator: SqlOperator.In,
        title: t`Project category`,
        options: [],
      }),
      new Filter({
        column: PlannedExpensesWhereColumn.ProjectStageId,
        operator: SqlOperator.In,
        title: t`Project stage`,
        options: [],
      }),
      new Filter({
        column: PlannedExpensesWhereColumn.ProjectStatus,
        operator: SqlOperator.In,
        title: t`Project status`,
        options: [
          {
            value: ProjectStatusEnum.Active,
            label: t`Active`,
          },
          {
            value: ProjectStatusEnum.Finished,
            label: t`Finished`,
          },
          {
            value: ProjectStatusEnum.Archived,
            label: t`Archived`,
          },
        ],
      }),
      new Filter({
        column: PlannedExpensesWhereColumn.IncludeDrafts,
        title: t`Include drafts`,
        operator: SqlOperator.Eq,
        options: getBooleanFilterOptions(),
      }),
      new Filter({
        column: PlannedExpensesWhereColumn.ProjectType,
        operator: SqlOperator.Eq,
        title: t`Type`,
        options: [
          {
            value: ProjectTypeEnum.FixedPrice,
            label: t`Fixed-price`,
          },
          {
            value: ProjectTypeEnum.TimeMaterials,
            label: t`Time & Materials`,
          },
        ],
      }),
      new Filter({
        column: PlannedExpensesWhereColumn.TeamId,
        operator: SqlOperator.In,
        title: t`Team`,
        options: [],
      }),

      new Filter({
        column: PlannedExpensesWhereColumn.BudgetItemType,
        operator: SqlOperator.In,
        title: t`Budget item type`,
        options: getBudgetItemTypeFilterOptions(),
      }),
      new Filter({
        column: PlannedExpensesWhereColumn.HasPlannedExpenses,
        operator: SqlOperator.Eq,
        title: t`Has planned expenses`,
        options: getBooleanFilterOptions(),
      }),
    ],
    {
      onChange: () => {
        this.pagination.resetPage();
        this.fetchSummary();
      },
      tableKey: "planned-expenses",
      orderBy: this.orderBy,
    },
  );

  constructor() {
    makeObservable(this);
    this.hydrateFromUrl(Router.query);
    this.fetchFilterOptions();
    this.fetchSummary();
  }

  @computed
  get queryVariables() {
    return commonQueryVariables({
      orderBy: this.orderBy,
      where: this.where,
      searchTerm: this.searchTerm,
      pagination: this.pagination,
    });
  }

  @computed
  get searchParams() {
    return commonSearchParams({
      orderBy: this.orderBy,
      where: this.where,
      searchTerm: this.searchTerm,
      pagination: this.pagination,
    });
  }

  @computed
  get activeOrderBy() {
    return this.orderBy.clauses[0];
  }

  @computed
  get isLoading(): boolean {
    return this.overviewFetcher.isLoading.value;
  }

  @computed
  get isLoadingSummary(): boolean {
    return this.summaryFetcher.isLoading.value;
  }

  @action.bound
  async fetchTableData(): Promise<void> {
    const [data, error] = await this.overviewFetcher.fetch(this.queryVariables);
    if (error || !data.plannedExpensesOverview?.data) return;

    this.tableData = data.plannedExpensesOverview.data.map((item) => ({
      id: item.id,
      rowType: "project" as const,
      project: item,
      __subRows: item.budget_items.map((subRow) => ({
        id: subRow.id,
        rowType: "subRow" as const,
        project: item,
        subRow,
      })),
    }));
    this.pagination.setFromPaginatorInfo(
      data.plannedExpensesOverview.paginatorInfo,
    );
  }

  @action.bound
  async fetchSummary(): Promise<void> {
    const [data, error] = await this.summaryFetcher.fetch({
      filters: this.queryVariables.filters,
    });
    if (error) return;

    this.summary = data.plannedExpensesOverviewSummary;
  }

  @action.bound
  async fetchFilterOptions(): Promise<void> {
    const [data, error] = await this.filterOptionsFetcher.fetch();
    if (error) return;

    this.where.filtersByColumn
      .get(PlannedExpensesWhereColumn.ProjectManagerId)
      ?.setOptions(
        mapToOptions.projectManagers.toOptions(data.projectManagerSimpleMap),
      );
    this.where.filtersByColumn
      .get(PlannedExpensesWhereColumn.BrandId)
      ?.setOptions(mapToOptions.brandsSectioned(data.clientsSimpleMap));
    this.where.filtersByColumn
      .get(PlannedExpensesWhereColumn.TeamId)
      ?.setOptions(mapToOptions.teams(data.teamSimpleMap));
    this.where.filtersByColumn
      .get(PlannedExpensesWhereColumn.ProjectCategoryId)
      ?.setOptions(
        data.projectCategories.map((category) => ({
          value: category.id,
          label: category.name,
        })),
      );
    this.where.filtersByColumn
      .get(PlannedExpensesWhereColumn.ProjectStageId)
      ?.setOptions(
        data.projectStages.map((stage) => ({
          value: stage.id,
          label: stage.name,
        })),
      );

    this.where.filtersByColumn
      .get(PlannedExpensesWhereColumn.ProjectId)
      ?.setOptions(mapToOptions.projects(data.projectsSimpleMap));
  }

  @action.bound
  handleOrderChange(clause: {
    column: PlannedExpensesOrderByColumnEnum;
    order: SortOrder | undefined;
  }) {
    if (!clause.order) {
      this.orderBy.setClauses([]);
      this.pagination.resetPage();
      return;
    }
    this.orderBy.setClauses([{ column: clause.column, order: clause.order }]);
    this.pagination.resetPage();
  }

  @action.bound
  handleSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.pagination.resetPage();
    this.fetchSummary();
  }

  @action.bound
  hydrateFromUrl(query: Query) {
    this.searchTerm = getQueryParamOptional("search", query) ?? "";
    this.where.fromURLSearchParam(query);
    this.orderBy.fromURLSearchParam(query);
    this.pagination.fromURLSearchParam(query);
  }
}
